import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n";
import { useQuery } from "@tanstack/react-query";
import { getVenues, getPlans, initiatePayment } from "@/api/endpoints";
import {
  Crown, Sparkles, ShoppingCart, FileDown, Globe, Star,
  Zap, BarChart3, Languages, Palette, BadgePercent, Check,
} from "lucide-react";

const PRO_FEATURES = [
  { icon: Sparkles, key: "upgrade_ai" },
  { icon: ShoppingCart, key: "upgrade_orders" },
  { icon: FileDown, key: "upgrade_pdf" },
  { icon: Globe, key: "upgrade_domain" },
  { icon: Star, key: "upgrade_loyalty" },
  { icon: Palette, key: "upgrade_themes" },
  { icon: Languages, key: "upgrade_languages" },
  { icon: BarChart3, key: "upgrade_analytics" },
  { icon: BadgePercent, key: "upgrade_branding" },
  { icon: Zap, key: "upgrade_unlimited" },
] as const;

export function UpgradeDialog({ open, onClose, feature }: { open: boolean; onClose: () => void; feature?: string }) {
  const { t, lang } = useI18n();
  const [loading, setLoading] = useState(false);
  const [checkoutHtml, setCheckoutHtml] = useState("");

  const { data: venues } = useQuery({ queryKey: ["venues"], queryFn: getVenues, enabled: open });
  const { data: plans } = useQuery({ queryKey: ["plans"], queryFn: getPlans, enabled: open });

  const proPlan = plans?.find((p) => p.type === "PRO");
  const venueId = venues?.[0]?.id || (venues?.[0] as unknown as Record<string, string>)?._id;

  const price = lang === "tr" ? (proPlan?.priceTryYearly || proPlan?.priceYearly || 0) : (proPlan?.priceYearly || 0);
  const currencySymbol = lang === "tr" ? "₺" : "$";

  useEffect(() => { if (!open) setCheckoutHtml(""); }, [open]);

  const handleUpgrade = async () => {
    if (!venueId || !proPlan) return;
    setLoading(true);
    try {
      const res = await initiatePayment({ venueId, planType: "PRO", period: "yearly" });
      if (res.checkoutFormContent) {
        setCheckoutHtml(res.checkoutFormContent);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  // iyzico checkout form
  if (checkoutHtml) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("upgrade_title")}</DialogTitle>
          </DialogHeader>
          <div
            className="min-h-[400px]"
            ref={(el) => {
              if (el && checkoutHtml) {
                el.innerHTML = checkoutHtml;
                const scripts = el.querySelectorAll("script");
                scripts.forEach((oldScript) => {
                  const newScript = document.createElement("script");
                  if (oldScript.src) newScript.src = oldScript.src;
                  else newScript.textContent = oldScript.textContent;
                  oldScript.parentNode?.replaceChild(newScript, oldScript);
                });
              }
            }}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        {/* Header gradient */}
        <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent px-6 pt-6 pb-4 text-center">
          <div className="mx-auto mb-3 flex size-16 items-center justify-center rounded-2xl bg-primary/15">
            <Crown className="size-8 text-primary" />
          </div>
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">{t("upgrade_title")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mt-2">
            {feature
              ? t("upgrade_feature_desc").replace("{{feature}}", feature)
              : t("upgrade_desc")}
          </p>
        </div>

        <div className="px-6 pb-6">
          {/* Welcome credits badge + Price */}
          {proPlan && (
            <div className="text-center my-4">
              {(proPlan.welcomeCredits ?? 0) > 0 && (
                <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-3 py-1 mb-3">
                  <Sparkles className="size-3.5 text-amber-500" />
                  <span className="text-xs font-bold text-amber-700">
                    {t("welcome_credits_badge").replace("{{credits}}", String(proPlan.welcomeCredits))}
                  </span>
                </div>
              )}
              <div>
                <span className="text-3xl font-black text-primary">
                  {currencySymbol}{price.toLocaleString()}
                </span>
                <span className="text-sm text-muted-foreground ml-1">{t("per_year")}</span>
              </div>
            </div>
          )}

          {/* Features grid */}
          <div className="grid grid-cols-2 gap-2 mb-5">
            {PRO_FEATURES.map(({ icon: Icon, key }) => (
              <div key={key} className="flex items-center gap-2.5 rounded-lg border p-2.5 text-left">
                <div className="flex size-7 items-center justify-center rounded-md bg-primary/10 shrink-0">
                  <Icon className="size-3.5 text-primary" />
                </div>
                <span className="text-xs font-medium leading-tight">{t(key as Parameters<typeof t>[0])}</span>
              </div>
            ))}
          </div>

          {/* CTA */}
          <Button className="w-full h-11 text-base font-bold gap-2" onClick={handleUpgrade} disabled={loading || !venueId}>
            <Check className="size-4" />
            {loading ? t("loading") : t("upgrade_cta")}
          </Button>
          <button className="mt-2 block w-full text-center text-xs text-muted-foreground hover:text-foreground" onClick={onClose}>
            {t("maybe_later")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Helper to check if an error is a plan limit error
export function isPlanError(err: unknown): boolean {
  const error = err as { response?: { status?: number; data?: { error?: string } } };
  return error?.response?.status === 403 || error?.response?.status === 402 ||
    error?.response?.data?.error === "AI_NOT_AVAILABLE" ||
    error?.response?.data?.error === "INSUFFICIENT_CREDITS";
}
